import { supabaseApi } from '../api'

type FileLike = Blob & { name?: string; size: number; type: string }

type UploadModelPhotoInput = {
  supplierCode: string
  material: string
  index: number
  file: FileLike
}

type GetModelPhotoInput = {
  supplierCode: string
  material: string
}

type RenameModelPhotoInput = {
  supplierCode: string
  material: string
  index: number
  targetIndex: number
}

type DeleteModelPhotoInput = {
  supplierCode: string
  material: string
  filename: string
}

const BUCKET_NAME = 'photos'
const MAX_FILE_SIZE_BYTES = 400 * 1000 // 400kb
const ALLOWED_MIME_TYPE = 'image/webp'
const FILE_EXTENSION = 'webp'

const normalizeSupplierCode = (value: string) => value.trim().replace(/\s+/g, '').toUpperCase()
const normalizeMaterial = (value: string) =>
  value
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/-/g, '_')
    .toLowerCase()

export const toStoragePath = ({
  supplierCode,
  material,
  filename,
}: {
  supplierCode: string
  material: string
  filename: string
}) => {
  const normalizedSupplierCode = normalizeSupplierCode(supplierCode)
  const normalizedMaterial = normalizeMaterial(material)
  return `${normalizedSupplierCode}/${normalizedMaterial}/${filename}`
}

export const buildPhotoFilename = ({
  supplierCode,
  index,
}: {
  supplierCode: string
  index: number
}) => {
  const normalizedSupplierCode = normalizeSupplierCode(supplierCode)
  const suffix = Math.random().toString(36).substring(2, 9)
  return `${normalizedSupplierCode}_${index}-${suffix}.${FILE_EXTENSION}`
}

export const buildPhotoStoragePath = ({
  supplierCode,
  material,
  index,
}: {
  supplierCode: string
  material: string
  index: number
}) => {
  const filename = buildPhotoFilename({ supplierCode, index })
  return toStoragePath({
    supplierCode,
    material,
    filename,
  })
}

const assertWithinFileLimit = (file: FileLike) => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(
      `File exceeds maximum allowed size of ${(MAX_FILE_SIZE_BYTES / (1024 * 1024)).toFixed(1)} MB`
    )
  }
}

const assertMimeType = (file: FileLike) => {
  if (file.type !== ALLOWED_MIME_TYPE) {
    throw new Error('Only WEBP images are supported')
  }
}

const assertFileDimensions = async (file: FileLike) => {
  const dimensionRatio = await new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      resolve(img.width / img.height)
      URL.revokeObjectURL(url)
    }
    img.src = url
  })
  if (dimensionRatio !== 1) {
    throw new Error('File dimensions must be a 1x1')
  }
}

const handleStorageError = (operation: string, error: unknown) => {
  if (error && typeof error === 'object' && 'message' in error) {
    throw new Error(
      `Supabase storage ${operation} failed: ${(error as { message: string }).message}`
    )
  }
  throw new Error(`Supabase storage ${operation} failed`)
}

const uploadModelPhoto = async ({ supplierCode, material, index, file }: UploadModelPhotoInput) => {
  assertMimeType(file)
  assertWithinFileLimit(file)
  await assertFileDimensions(file)

  const objectPath = buildPhotoStoragePath({
    supplierCode,
    material,
    index,
  })

  const { error, data } = await supabaseApi.storage.from(BUCKET_NAME).upload(objectPath, file, {
    cacheControl: '3600',
    upsert: true,
    contentType: file.type || ALLOWED_MIME_TYPE,
  })

  if (error) {
    handleStorageError('upload', error)
  }

  return {
    path: objectPath,
    data,
  }
}

const getModelPhotos = async ({ supplierCode, material }: GetModelPhotoInput) => {
  const path = `${supplierCode}/${material}`
  const { data, error } = await supabaseApi.storage.from(BUCKET_NAME).list(path)
  if (error) {
    console.error(error)
    return null
  }

  return data.map(
    (image) =>
      supabaseApi.storage.from(BUCKET_NAME).getPublicUrl(`${path}/${image.name}`).data.publicUrl
  )
}

export type RenameModelPhotoByPathInput = {
  sourcePath: string
  destinationPath: string
}

const renameModelPhotoByPath = async ({
  sourcePath,
  destinationPath,
}: RenameModelPhotoByPathInput) => {
  const { error, data } = await supabaseApi.storage
    .from(BUCKET_NAME)
    .move(sourcePath, destinationPath)

  if (error) {
    handleStorageError('rename', error)
  }

  return { path: destinationPath, data }
}

const deleteModelPhoto = async ({ supplierCode, material, filename }: DeleteModelPhotoInput) => {
  const path = toStoragePath({
    supplierCode,
    material,
    filename,
  })

  const { error, data } = await supabaseApi.storage.from(BUCKET_NAME).remove([path])

  if (error) {
    handleStorageError('delete', error)
  }

  return { path, data }
}

const listAllPhotos = async () => {
  const { data, error } = await supabaseApi.storage.from(BUCKET_NAME).list('', {
    limit: 1000,
    offset: 0,
  })
  if (error) {
    handleStorageError('list', error)
  }
  return data
}

export const bucketApi = {
  listAllPhotos,
  uploadModelPhoto,
  renameModelPhotoByPath,
  deleteModelPhoto,
  getModelPhotos,
}

export type BucketApi = typeof bucketApi
export type { UploadModelPhotoInput, RenameModelPhotoInput, DeleteModelPhotoInput }
